import { useContext, useState } from 'react';
import { nanoid } from 'nanoid';
import {
  deepClone,
  AccessibilityContext,
} from '@monash/portal-frontend-common';
import { Button, Icon, useResponsiveValue } from '@monash/portal-react';
import { MOBILE_RESPONSIVE } from '@/components/ui/main/Main';
import { WidgetContext } from '@/components/providers/WidgetProvider';

import DraggableWindow from '@/components/ui/draggable-window/DraggableWindow';
import SortableDragAndDropWrapper, {
  DND_TYPE,
} from '@/components/ui/drag-and-drop/SortableDragAndDropWrapper';
import { arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { EditCarparkRow } from './edit-carpark-row/EditCarparkRow';
import c from './edit-carparks.module.scss';
import { createPortal } from 'react-dom';
import { DragOverlay } from '@dnd-kit/core';

export const DROPDOWN_ITEM_CLASSNAME = 'carpark-dropdown-item';

export const EditCarparks = ({
  escapeFocusRef,
  initialCarparks,
  assembledParkingLocations,
  updateData,
}) => {
  const { editActiveWidget, setEditActiveWidget } = useContext(WidgetContext);
  const { setAppLivePoliteMsg, resetAppLiveMsgs } =
    useContext(AccessibilityContext);
  const responsiveSize = useResponsiveValue(MOBILE_RESPONSIVE);

  const [selectedCarparks, setSelectedCarparks] = useState(
    initialCarparks.length
      ? initialCarparks
      : [
          {
            id: nanoid(),
            value: '',
          },
        ]
  );

  // drag and drop
  const [activeId, setActiveId] = useState(null);

  const activeCarpark = selectedCarparks.find(
    (carpark) => carpark.id === activeId
  );
  // onDragStart
  const activateItem = (e) => {
    setActiveId(e.active.id);
  };

  // onDragCancel
  const deactivateActiveItem = () => {
    setActiveId(null);
  };

  const isMobile = responsiveSize === 'S';
  const HEADING_TEXT = 'Add/edit carparks';

  const getTriggerRefPosition = () => {
    if (!escapeFocusRef?.current) return;
    const position = escapeFocusRef.current.getBoundingClientRect();
    return { x: position.right, y: position.bottom };
  };

  const swapItemOrder = (e) => {
    const { active, over } = e;
    if (over && active.id !== over.id) {
      setSelectedCarparks((carparks) => {
        const newCarparks = deepClone(carparks);
        return arrayMove(
          newCarparks,
          newCarparks.findIndex((carpark) => carpark.id === active.id),
          newCarparks.findIndex((carpark) => carpark.id === over.id)
        );
      });
    }
    deactivateActiveItem();
  };

  const addNewCarpark = () => {
    const newCarpark = {
      value: '',
      id: nanoid(),
    };
    setSelectedCarparks((carparks) => [...carparks, newCarpark]);

    setTimeout(() => {
      const dropdowns = document.querySelectorAll(
        `.${DROPDOWN_ITEM_CLASSNAME} input`
      );
      dropdowns[dropdowns.length - 1].focus();
    }, 100);
  };

  const save = () => {
    updateData(
      'selectedCarparks',
      selectedCarparks.filter((carpark) => Boolean(carpark.value))
    );

    resetAppLiveMsgs();

    setTimeout(() => {
      setAppLivePoliteMsg('Clayton Parking widget, saved');
    }, 200);

    onClose();
  };

  const onClose = () => {
    escapeFocusRef.current?.focus();
    setEditActiveWidget(null);
  };

  return (
    <DraggableWindow
      fullScreen={isMobile}
      title={HEADING_TEXT}
      isShowing={editActiveWidget}
      focusTrapEnabled
      focusTrapSelectors="button:not([disabled]), input[type=text], input[type=url]"
      onClose={onClose}
      initialPosition={getTriggerRefPosition()}
      extraRootProps={{
        role: 'dialog',
        'aria-label': HEADING_TEXT,
      }}
      keyboardDismissalEnabled
    >
      <div className={c.wrapper}>
        <div className={c.content}>
          <div className={c.gridHeader}>
            <h4 className={c.name}>Name</h4>
          </div>

          <SortableDragAndDropWrapper
            sortableItems={selectedCarparks}
            onDragEnd={swapItemOrder}
            strategy={verticalListSortingStrategy}
            dndType={DND_TYPE.VERTICAL}
            onDragStart={activateItem}
          >
            <ul className={c.carparkList}>
              {selectedCarparks.map((selectedCarpark, i) => (
                <EditCarparkRow
                  key={selectedCarpark.id}
                  selectedCarpark={selectedCarpark}
                  selectedCarparks={selectedCarparks}
                  setSelectedCarparks={setSelectedCarparks}
                  assembledParkingLocations={assembledParkingLocations}
                />
              ))}
            </ul>
            {createPortal(
              <DragOverlay className={c.DragOverlayWrapper}>
                <EditCarparkRow
                  selectedCarpark={activeCarpark}
                  selectedCarparks={selectedCarparks}
                  setSelectedCarparks={setSelectedCarparks}
                  assembledParkingLocations={assembledParkingLocations}
                />
              </DragOverlay>,
              document.getElementById('root') || document.body
            )}
          </SortableDragAndDropWrapper>
        </div>

        <div className={c.actions}>
          <Button
            variant="text"
            size="small"
            icon={<Icon.Plus />}
            onClick={addNewCarpark}
            onKeyDown={(e) => e.stopPropagation()}
            mode="card"
          >
            Add carpark
          </Button>
          <div>
            <Button
              variant="text"
              size="small"
              onClick={onClose}
              onKeyDown={(e) => e.stopPropagation()}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={save}
              onKeyDown={(e) => e.stopPropagation()}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </DraggableWindow>
  );
};
